import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, History, CheckCircle, GitCompare, UserPlus,
  Calendar, Mail, Share2, ExternalLink, Copy,
} from "lucide-react";
import { CRON_PRESETS } from "@/config/dashboardConstants";

export default function DashboardDialogs({
  // Delete workflow
  deleteDialogOpen, setDeleteDialogOpen, workflowToDelete, handleDeleteWorkflow,
  // Versions
  versionsDialogOpen, setVersionsDialogOpen, versions, handleRestoreVersion,
  // Create team
  createTeamDialogOpen, setCreateTeamDialogOpen, newTeamName, setNewTeamName, handleCreateTeam,
  // Invite link
  inviteDialogOpen, setInviteDialogOpen, inviteLink, copyToClipboard,
  // Join team
  joinDialogOpen, setJoinDialogOpen, joinCode, setJoinCode, handleJoinTeam,
  // Compare
  compareDialogOpen, setCompareDialogOpen, compareSelected, toggleCompareSelection,
  optimizations, handleCompare, comparing, compareResult, setCompareResult, setCompareSelected,
  // Email invite
  emailInviteDialogOpen, setEmailInviteDialogOpen, inviteEmail, setInviteEmail, handleEmailInvite,
  // Share
  shareDialogOpen, setShareDialogOpen, shareUrl,
  // Schedule
  scheduleDialogOpen, setScheduleDialogOpen, workflows,
  newScheduleWorkflow, setNewScheduleWorkflow,
  newScheduleCron, setNewScheduleCron,
  newScheduleSuper, setNewScheduleSuper,
  handleCreateSchedule,
}) {
  return (
    <>
      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle>Delete Workflow</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">
              Are you sure you want to delete &quot;{workflowToDelete?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteWorkflow} className="bg-red-500 hover:bg-red-600 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions */}
      <Dialog open={versionsDialogOpen} onOpenChange={setVersionsDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-md">
          <DialogHeader>
            <DialogTitle>Workflow Versions</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">
              Current version: {versions.current_version || 1}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {versions.versions?.length > 0 ? (
              versions.versions.map((ver) => (
                <motion.div key={ver.version_id} whileHover={{ scale: 1.02 }} className="card-dark rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-base">Version {ver.version_number}</div>
                    <div className="text-sm text-[var(--qn-foreground-subtle)]">
                      {ver.optimization_count} optimizations &bull; {ver.avg_cost_reduction}% avg savings
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleRestoreVersion(ver.workflow_id, ver.version_id)}>Restore</Button>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-6 text-[var(--qn-foreground-subtle)]">No saved versions yet</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Team */}
      <Dialog open={createTeamDialogOpen} onOpenChange={setCreateTeamDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Start a team to collaborate on quantum workflow optimizations</DialogDescription>
          </DialogHeader>
          <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="Team name" className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="team-name-input" onKeyDown={(e) => e.key === "Enter" && handleCreateTeam()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTeamDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} className="btn-primary" data-testid="confirm-create-team-btn">
              <Plus className="w-4 h-4 mr-2" />Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Link */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle>Invite Link Generated</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Share this link with teammates. Expires in 7 days.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={inviteLink} readOnly className="bg-[var(--qn-background)] border-[var(--qn-border)] font-mono text-sm" data-testid="invite-link-input" />
            <Button onClick={() => copyToClipboard(inviteLink)} size="icon" className="btn-primary shrink-0" data-testid="copy-invite-btn"><Copy className="w-4 h-4" /></Button>
          </div>
          <DialogFooter><Button onClick={() => setInviteDialogOpen(false)} variant="outline">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Team */}
      <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle>Join a Team</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Paste the invite link or code you received from a teammate</DialogDescription>
          </DialogHeader>
          <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Paste invite link or code" className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="join-code-input" onKeyDown={(e) => e.key === "Enter" && handleJoinTeam()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleJoinTeam} className="btn-primary" data-testid="confirm-join-btn"><UserPlus className="w-4 h-4 mr-2" />Join Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Optimizations */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitCompare className="w-5 h-5 text-[var(--qn-primary)]" />Compare Optimizations</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Select 2-5 optimization runs to compare side by side</DialogDescription>
          </DialogHeader>
          {!compareResult ? (
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {optimizations.map((opt) => (
                  <motion.div
                    key={opt.optimization_id}
                    whileHover={{ scale: 1.01 }}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${compareSelected.includes(opt.optimization_id) ? "bg-[var(--qn-primary-muted)] border-2 border-[var(--qn-primary)]" : "card-dark border border-[var(--qn-border)]"}`}
                    onClick={() => toggleCompareSelection(opt.optimization_id)}
                    data-testid={`compare-opt-${opt.optimization_id}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${compareSelected.includes(opt.optimization_id) ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]" : "border-[var(--qn-border)]"}`}>
                      {compareSelected.includes(opt.optimization_id) && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-mono">{opt.optimization_id}</div>
                      <div className="text-sm text-[var(--qn-foreground-subtle)]">{opt.solver_type} &middot; {new Date(opt.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-mono text-[var(--qn-success)]">-{opt.cost_reduction_percent}% cost</div>
                      <div className="text-sm text-[var(--qn-foreground-subtle)]">{opt.optimization_time_ms}ms</div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCompareDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCompare} className="btn-primary" disabled={compareSelected.length < 2 || comparing} data-testid="run-compare-btn">
                  {comparing ? "Comparing..." : `Compare ${compareSelected.length} Runs`}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="card-dark rounded-lg p-4 text-center">
                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Best Cost Reduction</div>
                  <div className="text-2xl font-mono font-bold text-[var(--qn-success)]">{compareResult.summary.best_cost_reduction}%</div>
                  <div className="text-sm text-[var(--qn-foreground-subtle)] font-mono">{compareResult.summary.best_cost_optimization}</div>
                </div>
                <div className="card-dark rounded-lg p-4 text-center">
                  <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Best Time Reduction</div>
                  <div className="text-2xl font-mono font-bold text-[var(--qn-primary)]">{compareResult.summary.best_time_reduction}%</div>
                  <div className="text-sm text-[var(--qn-foreground-subtle)] font-mono">{compareResult.summary.best_time_optimization}</div>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-[var(--qn-border)]">
                <table className="w-full text-base">
                  <thead>
                    <tr className="bg-[var(--qn-surface)] border-b border-[var(--qn-border)]">
                      <th className="text-left px-4 py-3 font-outfit text-sm">Run</th>
                      <th className="text-right px-4 py-3 font-outfit text-sm">Cost</th>
                      <th className="text-right px-4 py-3 font-outfit text-sm">Reduction</th>
                      <th className="text-right px-4 py-3 font-outfit text-sm">Time (ms)</th>
                      <th className="text-right px-4 py-3 font-outfit text-sm">Solver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareResult.optimizations.map((opt) => {
                      const isBestCost = opt.optimization_id === compareResult.summary.best_cost_optimization;
                      const isBestTime = opt.optimization_id === compareResult.summary.best_time_optimization;
                      return (
                        <tr key={opt.optimization_id} className="border-b border-[var(--qn-border)] last:border-0">
                          <td className="px-4 py-3"><div className="font-mono text-sm">{opt.optimization_id}</div><div className="text-sm text-[var(--qn-foreground-subtle)]">{new Date(opt.created_at).toLocaleString()}</div></td>
                          <td className="text-right px-4 py-3 font-mono">${opt.optimized_cost?.toFixed(4)}{isBestCost && <CheckCircle className="w-3 h-3 inline ml-1 text-[var(--qn-success)]" />}</td>
                          <td className="text-right px-4 py-3 font-mono text-[var(--qn-success)]">{opt.cost_reduction_percent}%</td>
                          <td className="text-right px-4 py-3 font-mono">{opt.optimization_time_ms}{isBestTime && <CheckCircle className="w-3 h-3 inline ml-1 text-[var(--qn-primary)]" />}</td>
                          <td className="text-right px-4 py-3"><Badge variant="outline" className="text-sm border-[var(--qn-border)]">{opt.solver_type}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-center text-base text-[var(--qn-foreground-muted)]">
                Average savings: <span className="font-mono text-[var(--qn-success)]">{compareResult.summary.avg_cost_reduction}%</span> cost &middot;{" "}
                <span className="font-mono text-[var(--qn-primary)]">{compareResult.summary.avg_time_reduction}%</span> time
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCompareResult(null); setCompareSelected([]); }}>New Comparison</Button>
                <Button onClick={() => setCompareDialogOpen(false)} className="btn-primary">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Invite */}
      <Dialog open={emailInviteDialogOpen} onOpenChange={setEmailInviteDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[var(--qn-primary)]" />Invite by Email</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Send a team invite directly to a colleague&apos;s email</DialogDescription>
          </DialogHeader>
          <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" type="email" className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="email-invite-input" onKeyDown={(e) => e.key === "Enter" && handleEmailInvite()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailInviteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEmailInvite} className="btn-primary" data-testid="send-email-invite-btn"><Mail className="w-4 h-4 mr-2" />Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Workflow */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[var(--qn-primary)]" />Public Share Link</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Anyone with this link can view the workflow and optimization results</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={shareUrl} readOnly className="bg-[var(--qn-background)] border-[var(--qn-border)] font-mono text-sm" data-testid="share-url-input" />
            <Button onClick={() => copyToClipboard(shareUrl)} size="icon" className="btn-primary shrink-0" data-testid="copy-share-btn"><Copy className="w-4 h-4" /></Button>
          </div>
          <div className="flex items-center gap-2 text-sm text-[var(--qn-foreground-muted)]">
            <ExternalLink className="w-3.5 h-3.5" />
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--qn-primary)]">Open in new tab</a>
          </div>
          <DialogFooter><Button onClick={() => setShareDialogOpen(false)} className="btn-primary">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-[var(--qn-secondary)]" />New Scheduled Optimization</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Automatically optimize a workflow on a recurring schedule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-base font-medium mb-2 block">Workflow</label>
              <Select value={newScheduleWorkflow} onValueChange={setNewScheduleWorkflow}>
                <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="schedule-workflow-select"><SelectValue placeholder="Select workflow" /></SelectTrigger>
                <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  {workflows.map((wf) => (<SelectItem key={wf.workflow_id} value={wf.workflow_id}>{wf.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-base font-medium mb-2 block">Frequency</label>
              <Select value={newScheduleCron} onValueChange={setNewScheduleCron}>
                <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="schedule-cron-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  {CRON_PRESETS.map((preset) => (<SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-base font-medium">Quantum Super Optimize</label>
              <Switch checked={newScheduleSuper} onCheckedChange={setNewScheduleSuper} data-testid="schedule-super-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateSchedule} className="btn-primary" data-testid="confirm-create-schedule-btn"><Calendar className="w-4 h-4 mr-2" />Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
